import React, { useState, useEffect } from 'react';
import dashboard from './Dashboard.module.css';
import '@flaticon/flaticon-uicons/css/all/all.css';
import { signOut } from 'firebase/auth';
import { auth, database } from '../../firebase';
import { useNavigate } from "react-router-dom";
import { ref, onValue, remove, push, set, get } from 'firebase/database';

const Dashboard = () => {
    const [pages, setPages] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isNewPageModalOpen, setIsNewPageModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [userName, setUserName] = useState('');
    const itemsPerPage = 5;
    const navigate = useNavigate();

    useEffect(() => {
        const userId = auth.currentUser.uid;
        const userRef = ref(database, `Users/${userId}`);
        onValue(userRef, (snapshot) => {
            const userData = snapshot.val();
            setUserName(userData.Name);
        });

        const pagesRef = ref(database, `Users/${userId}/Pages`);
        onValue(pagesRef, async (snapshot) => {
            const pagesData = snapshot.val();
            const pagesList = [];

            for (let pageId in pagesData) {
                const pageRef = ref(database, `Pages/${pageId}`);
                const pageSnapshot = await get(pageRef);
                const pageData = pageSnapshot.val();
                if (pageData) {
                    pagesList.push({ id: pageId, ...pageData });
                }
            }
            setPages(pagesList);
        });
    }, []);

    const handleOpen = (id, event) => {
        event.stopPropagation();
        navigate(`/page/${id}`);
    };

    const handleDelete = async (id, event) => {
        event.stopPropagation();
        const userId = auth.currentUser.uid;
        await remove(ref(database, `Pages/${id}`));
        await remove(ref(database, `Users/${userId}/Pages/${id}`));

        // Update the pages state after deletion
        setPages((prevPages) => prevPages.filter((page) => page.id !== id));
    };

    const filteredPages = pages.filter(page => page?.Name?.toLowerCase().includes(searchQuery.toLowerCase()));
    const handleEdit = (item, event) => {
        event.stopPropagation();
        setCurrentItem(item);
        setIsEditModalOpen(true);
    };

    const handleSave = async () => {
        if (currentItem) {
            const pageRef = ref(database, `Pages/${currentItem.id}`);
            await set(pageRef, {
                ...currentItem,
                Name: currentItem.Name
            });

            // Update the pages state with the new name without adding duplicates
            setPages((prevPages) =>
                prevPages.map((page) =>
                    page.id === currentItem.id ? { ...page, Name: currentItem.Name } : page
                )
            );

            setIsEditModalOpen(false);
        }
    };

    const handleCreatePage = async (pageName) => {
        const userId = auth.currentUser.uid;
        const newPageRef = push(ref(database, 'Pages'));
        const pageId = newPageRef.key;

        await set(newPageRef, {
            Name: pageName,
            'ownerID': userId,
            'published': false,
            'sessionID': null,
            Content: {}
        });

        await set(ref(database, `Users/${userId}/Pages/${pageId}`), true);
        setIsNewPageModalOpen(false);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredPages.slice(indexOfFirstItem, indexOfLastItem);

    const totalPages = Math.ceil(filteredPages.length / itemsPerPage);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxPageNumbersToShow = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPageNumbersToShow - 1);

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(
                <button
                    key={i}
                    onClick={() => handlePageClick(i)}
                    className={`pagination-button ${i === currentPage ? 'active' : ''}`}
                >
                    {i}
                </button>
            );
        }

        return pageNumbers;
    };

    return (
        <div className={dashboard['dashboard-container']}>
            <div className={dashboard['title-card']}>
                <div className={dashboard['left-title-card']}>
                    <div className={dashboard['title-card-title']}>
                        <h2>Hello, {userName}</h2>
                    </div>
                </div>
                <div className={dashboard['title-card-actions']}>
                    <div className={dashboard['square-button']} title="Account" onClick={() => navigate('/account')}>
                        <i className="fi fi-rr-user"></i>
                    </div>
                    <div className={dashboard['square-button']} title="Log Out" onClick={() => {
                        signOut(auth).then(() => navigate('/'));
                    }}>
                        <i className="fi fi-rr-sign-out-alt"></i>
                    </div>
                </div>
            </div>

            <div className={dashboard['search-bar']}>
                <input
                    type="text"
                    placeholder="Search pages..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className={dashboard['add-button']} onClick={() => setIsNewPageModalOpen(true)}>
                    <i className="fi fi-rr-plus"></i> Add
                </button>
            </div>

            <div className={dashboard['link-table']}>
                {currentItems.map((item) => (
                    <div key={item.id} className={dashboard['link-card']} onClick={(e) => handleOpen(item.id, e)}>
                        <div className={dashboard['link-title']}>{item.Name}</div>
                        <div className={dashboard['link-actions']}>
                            <div onClick={(e) => handleOpen(item.id, e)} title="Open"
                                 className={dashboard['square-button']}>
                                <i className="fi fi-rr-folder-open"></i>
                            </div>
                            <div onClick={(e) => handleEdit(item, e)} title="Edit"
                                 className={dashboard['square-button']}>
                                <i className="fi fi-rr-edit"></i>
                            </div>
                            <div onClick={(e) => handleDelete(item.id, e)} title="Delete"
                                 className={dashboard['square-button']}>
                                <i className="fi fi-rr-trash"></i>
                            </div>
                        </div>
                    </div>))}
            </div>

            <div className={dashboard['pagination']}>
                <button onClick={handlePreviousPage} disabled={currentPage === 1}>
                    Previous
                </button>
                {renderPageNumbers()}
                <button onClick={handleNextPage} disabled={currentPage === totalPages}>
                    Next
                </button>
            </div>

            {isEditModalOpen && (
                <div className={dashboard['modal']}>
                    <div className={dashboard['modal-content']}>
                        <h3>Edit Page</h3>
                        <div className={dashboard['form-group']}>
                            <label>Name</label>
                            <input
                                type="text"
                                value={currentItem?.Name}
                                onChange={(e) => setCurrentItem({...currentItem, Name: e.target.value})}
                            />
                        </div>
                        <div className={dashboard['modal-actions']}>
                            <button className={dashboard['square-button']} onClick={handleSave} title="Save">
                                <i className="fi fi-rr-check"></i>
                            </button>
                            <button className={dashboard['square-button']} onClick={() => setIsEditModalOpen(false)} title="Cancel">
                                <i className="fi fi-rr-cross"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isNewPageModalOpen && (
                <div className={dashboard['modal']}>
                    <div className={dashboard['modal-content']}>
                        <h2>New Page</h2>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            handleCreatePage(e.target.elements.pageName.value);
                        }}>
                            <div className={dashboard['form-group']}>
                                <label>Page Name</label>
                                <input type="text" name="pageName" placeholder="Enter page name" required />
                            </div>
                            <div className={dashboard['modal-actions']}>
                                <button type="submit" className={dashboard['square-button']} title="Save">
                                    <i className="fi fi-rr-check"></i>
                                </button>
                                <button type="button" className={dashboard['square-button']}
                                        onClick={() => setIsNewPageModalOpen(false)} title="Cancel">
                                    <i className="fi fi-rr-cross"></i>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
export default Dashboard;